import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { adminCreatePromoCode, adminGetPromoCode, adminUpdatePromoCode, products } from '../../api/pricing';
import { todayIso } from '../../shared/data/common';
import AdminShell, { useAdminAccess } from './AdminShell';
import { Field, Section } from './PackageFormPage';
import { describeDiscount, emptyPromoForm, promoKinds, toPromoForm, toPromoRequest } from './promoForm';

export default function PromoCodeFormPage() {
  const { promoCodeId } = useParams();
  const navigate = useNavigate();
  const { auth } = useAuth();
  const hasAccess = useAdminAccess();
  const [form, setForm] = useState(emptyPromoForm);
  const [loading, setLoading] = useState(Boolean(promoCodeId));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!promoCodeId || !hasAccess) return undefined;
    let active = true;
    adminGetPromoCode(auth.accessToken, promoCodeId)
      .then((promo) => active && setForm(toPromoForm(promo)))
      .catch((requestError) => active && setError(requestError.message))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [promoCodeId, hasAccess, auth?.accessToken]);

  const set = (field) => (event) => {
    const { type, checked, value } = event.target;
    setForm((f) => ({ ...f, [field]: type === 'checkbox' ? checked : value }));
  };
  const toggleProduct = (name) => setForm((f) => ({
    ...f,
    products: f.products.includes(name) ? f.products.filter((p) => p !== name) : [...f.products, name],
  }));
  const allSelected = form.products.length === products.length;

  const submit = async (event) => {
    event.preventDefault();
    if (!form.products.length) {
      setError('Select at least one product this code can be used on.');
      window.scrollTo(0, 0);
      return;
    }
    setBusy(true);
    setError('');
    try {
      const body = toPromoRequest(form);
      const saved = promoCodeId
        ? await adminUpdatePromoCode(auth.accessToken, promoCodeId, body)
        : await adminCreatePromoCode(auth.accessToken, body);
      navigate('/admin/promo-codes', { state: { message: `Promo code ${saved.code} was saved.` } });
    } catch (requestError) {
      setError(requestError.message);
      window.scrollTo(0, 0);
    } finally {
      setBusy(false);
    }
  };

  const isPercent = form.discountType === 'Percentage';

  return (
    <AdminShell title={promoCodeId ? `Edit ${form.code || 'promo code'}` : 'New promo code'} subtitle={promoKinds[form.kind].blurb}>
      {loading ? <p role="status">Loading promo code…</p> : (
        <form className="admin-form" onSubmit={submit}>
          {error && <p className="api-message error" role="alert">{error}</p>}

          <div className="segmented" role="radiogroup" aria-label="Promo type">
            {Object.entries(promoKinds).map(([key, info]) => (
              <label key={key} className={form.kind === key ? 'active' : ''}>
                <input type="radio" name="kind" value={key} checked={form.kind === key} onChange={set('kind')} />
                {info.label}
              </label>
            ))}
          </div>

          <Section title="Offer details">
            <div className="form-grid">
              <Field label="Promo code" hint="3–20 letters or digits. Customers type this at checkout.">
                <input required pattern="[A-Za-z0-9]{3,20}" maxLength="20" value={form.code}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="e.g. DIWALI26" />
              </Field>
              {form.kind === 'Festival' && (
                <Field label="Festival name">
                  <input required maxLength="60" value={form.festivalName} onChange={set('festivalName')} placeholder="e.g. Diwali" />
                </Field>
              )}
              <Field label="Offer title" hint="Shown to customers in the offers list" wide>
                <input required minLength="3" maxLength="100" value={form.title} onChange={set('title')} placeholder="e.g. Diwali special: 20% off, up to ₹3,000" />
              </Field>
              <Field label="Terms / description (optional)" wide>
                <textarea rows="2" maxLength="300" value={form.description} onChange={set('description')} />
              </Field>
            </div>
          </Section>

          <Section title="Products" hint="Where customers can use this code.">
            <div className="check-grid">
              <label className="check">
                <input type="checkbox" checked={allSelected}
                  onChange={() => setForm((f) => ({ ...f, products: allSelected ? [] : products.map(([, name]) => name) }))} />
                <span><b>All products</b></span>
              </label>
              {products.map(([, name]) => (
                <label key={name} className="check">
                  <input type="checkbox" checked={form.products.includes(name)} onChange={() => toggleProduct(name)} />
                  <span>{name}</span>
                </label>
              ))}
            </div>
          </Section>

          <Section title="Discount" hint="Applied to the booking amount (fare and taxes), before the convenience fee.">
            <div className="form-grid three">
              <Field label="Discount type">
                <select value={form.discountType} onChange={set('discountType')}>
                  <option value="Percentage">Percentage off</option>
                  <option value="Flat">Flat amount off</option>
                </select>
              </Field>
              <Field label={isPercent ? 'Percent off' : 'Amount off (₹)'}>
                <input required type="number" min="1" max={isPercent ? 100 : undefined} step="any" value={form.discountValue} onChange={set('discountValue')} />
              </Field>
              {isPercent && (
                <Field label="Maximum discount (₹)" hint="Optional cap">
                  <input type="number" min="1" step="1" value={form.maxDiscount} onChange={set('maxDiscount')} />
                </Field>
              )}
              <Field label="Minimum booking value (₹)" hint="Optional">
                <input type="number" min="0" step="1" value={form.minBookingAmount} onChange={set('minBookingAmount')} />
              </Field>
            </div>
            {form.discountValue && <p className="muted small">Customers get {describeDiscount(form)}.</p>}
          </Section>

          <Section title="Validity & limits" hint="The code stops working after the end date (India time). Leave limits empty for unlimited use.">
            <div className="form-grid three">
              <Field label="Valid from">
                <input required type="date" value={form.validFrom} onChange={set('validFrom')} />
              </Field>
              <Field label="Valid until">
                <input required type="date" min={promoCodeId ? form.validFrom : (form.validFrom > todayIso() ? form.validFrom : todayIso())}
                  value={form.validUntil} onChange={set('validUntil')} />
              </Field>
              <Field label="Total uses allowed" hint="Across all customers">
                <input type="number" min="1" step="1" value={form.usageLimit} onChange={set('usageLimit')} />
              </Field>
              <Field label="Uses per customer" hint={form.kind === 'FirstBooking' ? 'Always 1 for first-booking offers' : 'Per account or email'}>
                <input type="number" min="1" step="1" disabled={form.kind === 'FirstBooking'}
                  value={form.kind === 'FirstBooking' ? '1' : form.perCustomerLimit} onChange={set('perCustomerLimit')} />
              </Field>
            </div>
          </Section>

          <div className="panel admin-submit">
            <label className="check">
              <input type="checkbox" checked={form.isActive} onChange={set('isActive')} />
              <span><b>Active</b> — untick to stop the code working immediately</span>
            </label>
            <label className="check">
              <input type="checkbox" checked={form.showOnSite} onChange={set('showOnSite')} />
              <span><b>List on the checkout page</b> — untick for a private code that only works when typed</span>
            </label>
            <div className="admin-actions">
              <Link to="/admin/promo-codes" className="btn btn-outline">Cancel</Link>
              <button className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save promo code'}</button>
            </div>
          </div>
        </form>
      )}
    </AdminShell>
  );
}
